import { useLogin } from 'react-admin';
import { useEffect, useState, useMemo } from 'react';
import { Box, Card, CardContent, Typography, Button, Container } from '@mui/material';
import { styled } from '@mui/material/styles';
import LoginIcon from '@mui/icons-material/Login';
import { brandingService, Branding } from './services/brandingService';

interface LoginContainerProps {
    backgroundUrl?: string | null;
}

const LoginContainer = styled(Box, {
    shouldForwardProp: (prop) => prop !== 'backgroundUrl',
})<LoginContainerProps>(({ backgroundUrl }) => ({
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: backgroundUrl
        ? `url(${backgroundUrl}) center/cover no-repeat`
        : 'linear-gradient(135deg, #1f3240 0%, #1a2a35 100%)',
    position: 'relative',
    overflow: 'hidden',
    '&::before': {
        content: '""',
        position: 'absolute',
        width: '200%',
        height: '200%',
        background: backgroundUrl
            ? 'rgba(0, 0, 0, 0.4)'
            : 'radial-gradient(circle, rgba(255,255,255,0.1) 1px, transparent 1px)',
        backgroundSize: backgroundUrl ? '100% 100%' : '50px 50px',
        animation: backgroundUrl ? 'none' : 'float 30s linear infinite',
    },
    '@keyframes float': {
        '0%': { transform: 'translate(0, 0)' },
        '100%': { transform: 'translate(-50px, -50px)' },
    },
}));

const LoginCard = styled(Card)(({ theme }) => ({
    maxWidth: 480,
    width: '100%',
    padding: theme.spacing(2),
    borderRadius: 24,
    boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
    position: 'relative',
    zIndex: 1,
    backdropFilter: 'blur(10px)',
    background: 'rgba(255, 255, 255, 0.98)',
    animation: 'slideUp 0.5s ease-out',
    '@keyframes slideUp': {
        from: {
            opacity: 0,
            transform: 'translateY(30px)',
        },
        to: {
            opacity: 1,
            transform: 'translateY(0)',
        },
    },
}));

const Logo = styled(Box)({
    width: 200,
    height: 120,
    margin: '0 auto 24px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
});

interface LoginButtonProps {
    primaryColor?: string;
}

// Helper function to darken a hex color
const darkenColor = (hex: string, percent: number): string => {
    const num = parseInt(hex.replace('#', ''), 16);
    const amt = Math.round(2.55 * percent);
    const R = Math.max(0, (num >> 16) - amt);
    const G = Math.max(0, ((num >> 8) & 0x00FF) - amt);
    const B = Math.max(0, (num & 0x0000FF) - amt);
    return `#${(0x1000000 + R * 0x10000 + G * 0x100 + B).toString(16).slice(1)}`;
};

const LoginButton = styled(Button, {
    shouldForwardProp: (prop) => prop !== 'primaryColor',
})<LoginButtonProps>(({ theme, primaryColor }) => {
    const color = primaryColor || '#e72956';
    const darkColor = darkenColor(color, 20);
    return {
        marginTop: theme.spacing(3),
        padding: '14px 32px',
        fontSize: '16px',
        fontWeight: 600,
        borderRadius: 12,
        background: `linear-gradient(135deg, ${color} 0%, ${darkColor} 100%)`,
        textTransform: 'none',
        boxShadow: `0 8px 24px ${color}4D`,
        transition: 'all 0.3s ease',
        '&:hover': {
            background: `linear-gradient(135deg, ${darkColor} 0%, ${color} 100%)`,
            boxShadow: '0 12px 32px rgba(31, 50, 64, 0.4)',
            transform: 'translateY(-2px)',
        },
    };
});

const LoginPage = () => {
    const login = useLogin();

    // Check if user was explicitly logged out
    const wasLoggedOut = localStorage.getItem('user_logged_out') === 'true';
    // Note: auto_login_in_progress flag is already set by App.tsx if needed

    const [showLogoutMessage, setShowLogoutMessage] = useState(wasLoggedOut);
    const [autoLoggingIn, setAutoLoggingIn] = useState(!wasLoggedOut);
    const [branding, setBranding] = useState<Branding | null>(null);
    const [companyName, setCompanyName] = useState<string>('Online Academy');
    const [brandingLoaded, setBrandingLoaded] = useState(false);

    // Load branding by domain on mount
    useEffect(() => {
        const loadBranding = async () => {
            try {
                const response = await brandingService.getBrandingByDomain();
                setBranding(response.branding);
                setCompanyName(response.companyName);
            } catch (e) {
                console.error('Error loading branding:', e);
            } finally {
                setBrandingLoaded(true);
            }
        };
        loadBranding();
    }, []);

    // Update favicon if branding has one
    useEffect(() => {
        if (branding?.faviconUrl) {
            brandingService.updateFavicon(branding.faviconUrl);
        }
    }, [branding?.faviconUrl]);

    useEffect(() => {
        if (!wasLoggedOut) {
            // User was not explicitly logged out (came from SSO Dashboard)
            // Try auto-login with existing SSO session
            localStorage.removeItem('user_logged_out');
            login({}).catch(() => {
                // If auto-login fails, show login button
                localStorage.removeItem('auto_login_in_progress');
                setAutoLoggingIn(false);
            });

            // Safety timeout: if redirect to SSO doesn't happen within 8 seconds,
            // show the manual login button so the user isn't stuck on "Anmeldung läuft..."
            const timeout = setTimeout(() => {
                localStorage.removeItem('auto_login_in_progress');
                setAutoLoggingIn(false);
            }, 8000);

            return () => clearTimeout(timeout);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []); // Run once on mount - auto-login must not re-trigger on every render

    const handleLogin = () => {
        // Clear the logout flag before logging in
        localStorage.removeItem('user_logged_out');
        login({});
    };

    // Determine logo to show
    const logoUrl = branding?.logoUrl || '/images/online-academy-hoch-rgb-2.png';
    const primaryColor = branding?.primaryColor || '#e72956';
    const spinnerColor = primaryColor;

    return (
        <LoginContainer backgroundUrl={branding?.loginBackgroundUrl}>
            <Container maxWidth="sm">
                <LoginCard elevation={0}>
                    <CardContent sx={{ p: 4 }}>
                        <Logo>
                            <img
                                src={logoUrl}
                                alt={`${companyName} Logo`}
                                style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                            />
                        </Logo>

                        {autoLoggingIn ? (
                            <>
                                <Typography
                                    variant="h4"
                                    align="center"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 700,
                                        color: '#1f3240',
                                        mb: 2,
                                    }}
                                >
                                    Anmeldung läuft...
                                </Typography>
                                <Typography
                                    variant="body1"
                                    align="center"
                                    color="text.secondary"
                                    sx={{ mb: 3 }}
                                >
                                    Sie werden automatisch angemeldet
                                </Typography>
                                <Box sx={{ display: 'flex', justifyContent: 'center' }}>
                                    <div style={{
                                        width: '40px',
                                        height: '40px',
                                        border: '4px solid #f3f3f3',
                                        borderTop: `4px solid ${spinnerColor}`,
                                        borderRadius: '50%',
                                        animation: 'spin 1s linear infinite',
                                    }}></div>
                                </Box>
                                <style>{`
                                    @keyframes spin {
                                        0% { transform: rotate(0deg); }
                                        100% { transform: rotate(360deg); }
                                    }
                                `}</style>
                            </>
                        ) : showLogoutMessage ? (
                            <>
                                <Typography
                                    variant="h4"
                                    align="center"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 700,
                                        color: '#1f3240',
                                        mb: 2,
                                    }}
                                >
                                    Erfolgreich abgemeldet
                                </Typography>
                                <Typography
                                    variant="body1"
                                    align="center"
                                    color="text.secondary"
                                    sx={{ mb: 1 }}
                                >
                                    Ihre lokale Session wurde beendet.
                                </Typography>
                                <Typography
                                    variant="body2"
                                    align="center"
                                    color="text.secondary"
                                >
                                    Melden Sie sich erneut an, um fortzufahren.
                                </Typography>
                                <LoginButton
                                    fullWidth
                                    variant="contained"
                                    onClick={handleLogin}
                                    startIcon={<LoginIcon />}
                                    primaryColor={primaryColor}
                                >
                                    Mit SSO anmelden
                                </LoginButton>
                            </>
                        ) : (
                            <>
                                <Typography
                                    variant="h4"
                                    align="center"
                                    gutterBottom
                                    sx={{
                                        fontWeight: 700,
                                        background: 'linear-gradient(135deg, #1f3240 0%, #1a2a35 100%)',
                                        WebkitBackgroundClip: 'text',
                                        WebkitTextFillColor: 'transparent',
                                        mb: 2,
                                    }}
                                >
                                    Willkommen
                                </Typography>
                                <Typography
                                    variant="body1"
                                    align="center"
                                    color="text.secondary"
                                    sx={{ mb: 1 }}
                                >
                                    {companyName}
                                </Typography>
                                <Typography
                                    variant="body2"
                                    align="center"
                                    color="text.secondary"
                                >
                                    Melden Sie sich mit Ihrem SSO-Account an
                                </Typography>
                                <LoginButton
                                    fullWidth
                                    variant="contained"
                                    onClick={handleLogin}
                                    startIcon={<LoginIcon />}
                                    primaryColor={primaryColor}
                                >
                                    Mit SSO anmelden
                                </LoginButton>
                            </>
                        )}
                    </CardContent>
                </LoginCard>
            </Container>
        </LoginContainer>
    );
};

export default LoginPage;
